import { BigSwitch, ReactIcons } from "@kalyzee/kalyzee-ui";
import { useTriggerable } from "@kalyzee/kast-web-sdk/web-components";
import clsx from "clsx";
import { useCallback, useMemo, useRef, useState } from "react";
import { BannerButton } from "../../../../components/common/BannerButton";
import { CreateMeetingPopup } from "../../../../components/common/CreateMeetingPopup";
import { MasterUI } from "../../../../components/common/MasterUI";
import { MediaConfigPopup } from "../../../../components/common/MediaConfigPopup";
import { MediaGlobalControls, MediaSourcePicker } from "../../../../components/common/MediaSourcePicker";
import { PageMasterMode, usePageContextRef } from "../../../../components/navigation/PageContext";
import { AnimatedBackground } from "../../../../components/utils/AnimatedBackground";
import { SessionMode } from "../../../../contexts/appStore/hooks";
import { useAppStoreSelector } from "../../../../contexts/appStore/selectors";
import { useMeetingAccessibility, useMeetingSources } from "../../../../hooks/meeting";
import { useSettings } from "../../../../hooks/settings";
import { MeetingReplayView } from "../vod/MeetingReplayView";
import { EventsAgenda } from "./EventsAgenda";
import { InstantMeetingsAgenda } from "./InstantMeetingsAgenda";
import { JoinMeetingView } from "./JoinMeetingView";
import styles from "./list.module.css";
import { VodsAgenda } from "./VodsAgenda";
import { getGlobalQueryLabel, NavigateGlobalQuery } from "../../../../helpers/navigation";

/** Only holders of this permission may start an instant meeting. */
const CREATE_MEETING_ANY_PERMISSION = "meeting:create:any";

type AgendaView = "events" | "vods" | "instant";

/** Value of the `tab` query that selects the instant meetings view. */
const INSTANT_TAB_QUERY_VALUE = "instant";

const SWITCH_OPTIONS: { value: AgendaView; label: string }[] = [
  { value: "events", label: "Activités" },
  { value: "vods", label: "Replays" },
];

/** Shown to everyone: the tab lists the instant meetings you took part in, not only your own. */
const INSTANT_SWITCH_OPTION: { value: AgendaView; label: string } = { value: "instant", label: "Instantanés" };

export const MeetingListStudentPage = () => {
  const pageContextRef = usePageContextRef<SessionMode.MASTER>();
  const sources = useMeetingSources();
  const [settings] = useSettings();
  const disableMeetingList = settings?.ui?.disableMeetingList === true;

  const isMobile = useAppStoreSelector(state => state.isMobile);
  const userPermissions = useAppStoreSelector(state => state.user?.permissions);
  // The settings switch withdraws the whole feature — the tab included. Creating
  // one takes the permission on top; the tab itself does not, as it lists the
  // instant meetings you attended as much as the ones you started.
  const instantMeetingsEnabled = settings?.instantMeeting?.enabled !== false;
  const canCreateMeeting = instantMeetingsEnabled
    && userPermissions?.includes(CREATE_MEETING_ANY_PERMISSION) === true;

  const hasVisibleSources = useMemo(() => sources.some(s => s.visible), [sources]);

  const switchOptions = useMemo(
    () => instantMeetingsEnabled ? [...SWITCH_OPTIONS, INSTANT_SWITCH_OPTION] : SWITCH_OPTIONS,
    [instantMeetingsEnabled],
  );

  const logoContainerRef = useRef<HTMLImageElement>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [mediaConfigOpen, setMediaConfigOpen] = useState(false);

  // The `vodId` query drives the Replays tab ("_" sentinel selects the tab)
  // and, with a real meeting id, opens that replay as an overlay above the
  // list — which stays mounted so its filters/results/scroll survive.
  const rawVodId = pageContextRef.current?.searchParams?.get(getGlobalQueryLabel(NavigateGlobalQuery.VOD_ID)) ?? null;
  const rawTab = pageContextRef.current?.searchParams?.get(getGlobalQueryLabel(NavigateGlobalQuery.LIST_TAB)) ?? null;
  // `tab` wins when it names a tab — a replay opened from the instant meetings
  // keeps `tab=instant` so closing it lands back there. Otherwise `vodId` does:
  // a bare replay deep-link must land on the Replays tab.
  const fromInstantTab = rawTab === INSTANT_TAB_QUERY_VALUE && instantMeetingsEnabled;
  const view: AgendaView = fromInstantTab
    ? "instant"
    : rawVodId !== null ? "vods" : "events";
  const vodId = rawVodId && rawVodId !== "_" ? rawVodId : undefined;

  // Same data source as the /player route; no-ops while no replay is open.
  const accessibility = useMeetingAccessibility(vodId);

  useTriggerable(logoContainerRef, { clickCount: 3 }, () => pageContextRef.current?.openSettingsPopup());

  const onChangeView = useCallback((value: AgendaView) => {
    pageContextRef.current?.goTo(
      PageMasterMode.LIST,
      undefined,
      {
        replace: true,
        queries: {
          [getGlobalQueryLabel(NavigateGlobalQuery.VOD_ID)]: value === "vods" ? "_" : undefined,
          [getGlobalQueryLabel(NavigateGlobalQuery.LIST_TAB)]: value === "instant" ? INSTANT_TAB_QUERY_VALUE : undefined,
        },
      },
    );
  }, [pageContextRef]);

  // Close the replay overlay: land on the tab it was opened from — Replays by
  // default, Instantanés when the replay came from that tab — via replace.
  // In-app opens push over the tab query, so browser Back stays coherent;
  // deep-linked first entries stay inside the app. Not goToPrevious: it
  // rebuilds the previous pathname without its query (losing the tab) and can
  // target a stale non-list page.
  const onCloseVod = useCallback(() => {
    pageContextRef.current?.goTo(
      PageMasterMode.LIST,
      undefined,
      {
        replace: true,
        queries: {
          [getGlobalQueryLabel(NavigateGlobalQuery.VOD_ID)]: fromInstantTab ? undefined : "_",
          [getGlobalQueryLabel(NavigateGlobalQuery.LIST_TAB)]: fromInstantTab ? INSTANT_TAB_QUERY_VALUE : undefined,
        },
      },
    );
  }, [pageContextRef, fromInstantTab]);

  return (
    <MasterUI onBack={vodId ? onCloseVod : undefined} meetingName={vodId ? accessibility?.name : undefined}>
      {vodId && (
        <div className={styles.vodOverlay}>
          <MeetingReplayView accessibility={accessibility} />
        </div>
      )}
      <AnimatedBackground
        className={vodId ? styles.hiddenUnderOverlay : undefined}
        contentClassName={styles.backgroundHome}
      >
        {disableMeetingList ? <JoinMeetingView /> : (
        <div className={styles.mainHomeLogged}>
          <div className={styles.mainHomeLoggedContent}>
            <div className={clsx(styles.layout, !isMobile && !hasVisibleSources && styles.layoutNoMedia)}>
              {!isMobile && hasVisibleSources && (
                <div className={styles.mediaColumn}>
                  <MediaSourcePicker
                    sources={sources}
                    className={styles.mediaSelector}
                    // We can ignore gesture errors
                    // here because this component is only used to configure the sources
                    // it will be mounted again when joining the meeting
                    // and there wont be gesture errors anymore
                    ignoreGestureError
                  />
                  <MediaGlobalControls sources={sources} className={styles.globalControls} />
                </div>
              )}

              <div className={styles.mainColumn}>
                <div className={styles.switchColumn}>
                  <BigSwitch
                    className={styles.bigSwitch}
                    options={switchOptions}
                    value={view}
                    onChange={onChangeView}
                  />
                </div>

                <div className={styles.eventsColumn}>
                  {view === "events" && <EventsAgenda />}
                  {view === "vods" && <VodsAgenda />}
                  {view === "instant" && <InstantMeetingsAgenda />}
                </div>

                {!(isMobile && view === "vods") && (
                  <div className={styles.actionButtons}>
                    {isMobile && hasVisibleSources && (
                      <BannerButton
                        icon={<ReactIcons.Settings />}
                        title="Configurer mes médias"
                        subtitle={isMobile ? undefined : "Choisissez caméra et micro avant de rejoindre"}
                        background="linear-gradient(135deg, var(--color-meet-purple-2) 0%, var(--color-meet-purple-1) 100%)"
                        onClick={() => setMediaConfigOpen(true)}
                      />
                    )}
                    {canCreateMeeting && (
                      <BannerButton
                        icon={<ReactIcons.Timer />}
                        title="Créer un meeting instantané"
                        subtitle={isMobile ? undefined : "Démarrez un nouveau cours en quelques secondes"}
                        background="var(--color-element-glass-bg)"
                        style={{
                          color: "var(--color-agenda-title)",
                          ["--banner-icon-circle-bg" as any]: "var(--color-element-banner-icon-circle-bg)",
                        }}
                        onClick={() => setCreateOpen(true)}
                      />
                    )}
                    {!isMobile && !hasVisibleSources && (
                      <MediaGlobalControls sources={sources} className={styles.globalControls} />
                    )}
                  </div>
                )}
              </div>
            </div>

            <CreateMeetingPopup
              visible={createOpen}
              onClose={() => setCreateOpen(false)}
              onCreated={(meeting) => {
                setCreateOpen(false);
                // Pass `openShare` via react-router state so the meeting
                // page knows to auto-open the share popup on arrival.
                pageContextRef.current?.goTo(
                  PageMasterMode.MEETING_DASHBOARD,
                  { id: meeting._id },
                  { state: { openShare: true } },
                );
              }}
            />
            <MediaConfigPopup
              visible={mediaConfigOpen}
              onClose={() => setMediaConfigOpen(false)}
              sources={sources}
            />
          </div>
        </div>
        )}
      </AnimatedBackground>
    </MasterUI>
  );
};

